import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectTrigger } from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { CategoryTree } from '@/lib/graphql/generated/graphql';
import { cn } from '@/lib/utils';
import { CATEGORY_SELECTOR_STYLES } from '@/lib/utils/category-styles';
import {
  findCategoryById,
  formatCategoryText,
  matchesSearch,
} from '@/lib/utils/category-utils';
import { categoryTreeState } from '@/states/category-tree-map.state';
import { storeAtom } from '@/states/store.state';
import { Store } from '@/types/store';
import { useAtomValue } from 'jotai';
import { ChevronDown, Search } from 'lucide-react';

interface CategorySelectorProps {
  value: string | null;
  onValueChange: (category: CategoryTree) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  emptyContent?: React.ReactNode;
  skipTooltipProvider?: boolean;
  onOpenChange?: (open: boolean) => void;
}

interface CategoryNodeProps {
  category: CategoryTree;
  level: number;
  selectedId?: string;
  onSelect: (category: CategoryTree) => void;
  searchQuery: string;
}

const CategoryNode: React.FC<CategoryNodeProps> = ({
  category,
  level,
  selectedId,
  onSelect,
  searchQuery,
}) => {
  const store = useAtomValue(storeAtom);

  const hasSubCategories = useMemo(
    () => category.subCategories && category.subCategories.length > 0,
    [category.subCategories]
  );

  const isVisible = useMemo(
    () => matchesSearch(category, searchQuery),
    [category, searchQuery]
  );

  const isSelected = useMemo(
    () => selectedId === category.id,
    [selectedId, category.id]
  );

  const formattedLabel = useMemo(
    () => formatCategoryText(category.label),
    [category.label]
  );

  const paddingLeft = useMemo(() => `${level * 20 + 12}px`, [level]);

  const handleSelect = useCallback(() => {
    onSelect(category);
  }, [onSelect, category]);

  if (!isVisible) return null;

  return (
    <div className="w-full">
      <div
        className={`flex min-h-[40px] cursor-pointer items-center gap-2 rounded-md px-3 py-2 transition-colors hover:bg-accent hover:text-accent-foreground ${
          isSelected ? 'bg-accent font-medium text-accent-foreground' : ''
        }`}
        style={{ paddingLeft }}
        onClick={handleSelect}
      >
        <div className="flex h-4 w-4 flex-shrink-0 items-center justify-center">
          {hasSubCategories && (
            <ChevronDown className="h-3 w-3 text-muted-foreground" />
          )}
        </div>

        <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="max-w-[200px] truncate text-sm font-medium">
              {formattedLabel}
            </span>
            <div className="flex flex-shrink-0 items-center gap-1">
              {category.tva && store === Store.FR && (
                <Badge
                  variant="secondary"
                  className="whitespace-nowrap text-xs"
                >
                  TVA {category.tva}%
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {hasSubCategories &&
        category.subCategories &&
        category.subCategories.map((subCategory) => (
          <CategoryNode
            key={subCategory.id}
            category={subCategory}
            level={level + 1}
            selectedId={selectedId}
            onSelect={onSelect}
            searchQuery={searchQuery}
          />
        ))}
    </div>
  );
};

const MemoizedCategoryNode = React.memo(CategoryNode);

export const CategorySelector: React.FC<CategorySelectorProps> = ({
  value,
  onValueChange,
  placeholder = 'Sélectionner une catégorie...',
  className,
  disabled = false,
  emptyContent,
  skipTooltipProvider = false,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [optimisticValue, setOptimisticValue] = useState<CategoryTree | null>(
    null
  );

  const categoryTree = useAtomValue(categoryTreeState);

  const selectedCategory = useMemo(() => {
    return value && categoryTree.length > 0
      ? findCategoryById(categoryTree, value)
      : null;
  }, [value, categoryTree]);

  const currentCategory = useMemo(
    () => optimisticValue || selectedCategory,
    [optimisticValue, selectedCategory]
  );

  const currentValue = useMemo(
    () => optimisticValue?.id || value,
    [optimisticValue?.id, value]
  );

  const hasValue = currentValue && currentValue !== '';

  // Reset optimistic value when real value changes
  useEffect(() => {
    if (optimisticValue && value === optimisticValue.id) {
      setOptimisticValue(null);
    }
  }, [value, optimisticValue]);

  // Reset search when selector closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  useEffect(() => {
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  const handleSelect = useCallback(
    (category: CategoryTree) => {
      setOptimisticValue(category);
      onValueChange(category);
      setIsOpen(false);
      setSearchQuery('');
    },
    [onValueChange]
  );

  const displayText =
    currentValue && currentValue !== ''
      ? currentCategory
        ? currentCategory.label
        : currentValue
      : placeholder;

  return (
    <div className={className}>
      <Select open={isOpen} onOpenChange={setIsOpen}>
        <SelectTrigger
          forceClassName={CATEGORY_SELECTOR_STYLES.trigger}
          className="border-none"
          disabled={disabled}
        >
          <div className="flex w-full items-center justify-between gap-2">
            <div className="flex min-w-0 flex-1 items-center gap-2 truncate px-2">
              {skipTooltipProvider ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      style={{
                        maxWidth: '130px',
                      }}
                      className={`block max-w-full truncate text-sm ${
                        hasValue
                          ? 'text-foreground text-gray-700'
                          : 'text-gray-700 text-muted-foreground'
                      }`}
                    >
                      {hasValue ? (
                        formatCategoryText(displayText)
                      ) : (
                        <span className="flex items-center gap-1">
                          <span>{formatCategoryText(displayText)}</span>
                          {emptyContent}
                        </span>
                      )}
                    </span>
                  </TooltipTrigger>
                  {hasValue && (
                    <TooltipContent
                      side="top"
                      className="z-sidebar-dropdown max-w-[400px] break-words"
                    >
                      <p>{currentCategory?.id || displayText}</p>
                    </TooltipContent>
                  )}
                </Tooltip>
              ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        style={{
                          maxWidth: '130px',
                        }}
                        className={`block max-w-full truncate text-sm ${
                          hasValue
                            ? 'text-foreground text-gray-700'
                            : 'text-gray-700 text-muted-foreground'
                        }`}
                      >
                        {hasValue ? (
                          formatCategoryText(displayText)
                        ) : (
                          <span className="flex items-center gap-1">
                            <span>{formatCategoryText(displayText)}</span>
                            {emptyContent}
                          </span>
                        )}
                      </span>
                    </TooltipTrigger>
                    {hasValue && (
                      <TooltipContent
                        side="top"
                        className="z-sidebar-dropdown max-w-[400px] break-words"
                      >
                        <p>{currentCategory?.id || displayText}</p>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
          </div>
        </SelectTrigger>

        {isOpen && (
          <SelectContent
            className={cn(
              'z-sidebar-dropdown',
              CATEGORY_SELECTOR_STYLES.content
            )}
            align="start"
            viewportClassName="p-0"
          >
            <div className="sticky top-0 border-b bg-background p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
                <Input
                  placeholder={t('components.other.search_category')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-9 pl-10"
                  autoFocus
                />
              </div>
            </div>

            <div className="max-h-80">
              <div className="py-1">
                {categoryTree.map((category) => (
                  <MemoizedCategoryNode
                    key={category.id}
                    category={category}
                    level={0}
                    selectedId={currentValue ?? undefined}
                    onSelect={handleSelect}
                    searchQuery={searchQuery}
                  />
                ))}
              </div>
            </div>
          </SelectContent>
        )}
      </Select>
    </div>
  );
};
